import React, { useState } from "react";
import { useNavigate } from "react-router";
import LoginBackground from "../../assets/images/login.png";
import AuthService from "../../services/authService";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";

const Login: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await AuthService.login({ email, password });

      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      navigate("/users");
    } catch (err: any) {
      let errorMessage = err.message;
      if (errorMessage.includes("Unauthorized")) {
        errorMessage = "Access denied. Only administrators are allowed.";
      } else {
        errorMessage =
          err.response?.data?.message || "Invalid email or password.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const apiName = process.env.REACT_APP_API_URL || "";

  const isStage = apiName.includes("staging");

  if (isStage) {
    console.log("This is staging environment");
  }

  return (
    <div className="flex h-screen">
      <div
        className="hidden md:flex md:w-1/2 bg-fill bg-center relative"
        style={{ backgroundImage: `url(${LoginBackground})` }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <h3 aria-level={3} className="text-6xl font-bold text-white">
            12 REPS
          </h3>
          {isStage && <p className="text-1xl font-bold text-white">Stage</p>}
        </div>
      </div>
      <div className="flex flex-col justify-center items-center w-full md:w-1/2 bg-gray-100">
        <div className="bg-white p-8 shadow-lg rounded-lg max-w-sm w-full">
          <h1 className="text-2xl font-bold mb-4 text-center">Log in</h1>
          <form onSubmit={handleLogin} className="space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium">
                Enter your email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium">
                Enter your password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-2/3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 mt-1" />
                ) : (
                  <EyeIcon className="h-5 w-5 mt-1" />
                )}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-primary text-white font-semibold rounded hover:bg-primary-dark
                    focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            >
              {loading ? "Logging in..." : "Log in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
